import { invokeFunction } from '../api'
import { toApiRequestError } from '../errors'

export interface DemoRequest {
  request_id: string
  name: string
  email: string
  phone: string | null
  organization: string | null
  tier: string | null
  service_type:
    | 'general'
    | 'mock_survey'
    | 'domain_audit'
    | 'qapi_design'
    | 'accreditation_prep'
    | 'compliance_retainer'
    | 'essentials_bundle'
    | 'accreditation_ready_bundle'
    | 'total_compliance_bundle'
    | null
  source: 'cal_com' | 'vapi_phone' | 'chat_widget' | 'manual'
  status: 'new' | 'contacted' | 'qualified' | 'demo_scheduled' | 'closed'
  notes: string | null
  cal_booking_id: string | null
  vapi_call_id: string | null
  call_duration_seconds: number | null
  call_summary: string | null
  chat_transcript_summary: string | null
  crm_person_id: string | null
  crm_synced_at: string | null
  n8n_triggered_at: string | null
  created_at: string
  updated_at: string
}

export interface CrmHealthStatus {
  demo_requests_api: 'healthy' | 'unreachable'
  cal_webhook: 'healthy' | 'unreachable'
  vapi_webhook: 'healthy' | 'unreachable' | 'not_configured'
  chat_transcript: 'healthy' | 'unreachable'
  twenty_crm: 'healthy' | 'unreachable' | 'not_configured'
  n8n_trigger: 'healthy' | 'unreachable' | 'not_configured'
}

export async function getDemoRequests(status?: string, serviceType?: string): Promise<DemoRequest[]> {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (serviceType) params.set('service_type', serviceType)
  const path = params.size > 0 ? `/?${params.toString()}` : '/'
  const { data, error } = await invokeFunction<{ demo_requests: DemoRequest[] }>('demo-requests', {
    method: 'GET',
    path,
  })
  if (error) throw toApiRequestError(error, 'Failed to load demo requests')
  return data?.demo_requests ?? []
}

export async function updateDemoRequest(
  requestId: string,
  updates: { status?: string; notes?: string; tier?: string }
): Promise<void> {
  const { error } = await invokeFunction('demo-requests', {
    method: 'PATCH',
    path: `/${requestId}`,
    body: updates,
  })
  if (error) throw toApiRequestError(error, 'Failed to update demo request')
}

export async function getCrmHealth(): Promise<CrmHealthStatus> {
  const { data, error } = await invokeFunction<{ health: CrmHealthStatus }>('demo-requests', {
    method: 'GET',
    path: '/health',
  })
  if (error) throw toApiRequestError(error, 'Failed to check CRM health')
  return data?.health ?? {
    demo_requests_api: 'unreachable',
    cal_webhook: 'unreachable',
    vapi_webhook: 'not_configured',
    chat_transcript: 'unreachable',
    twenty_crm: 'not_configured',
    n8n_trigger: 'not_configured',
  }
}

export async function syncToCrm(requestId: string): Promise<{ crm_person_id: string }> {
  const { data, error } = await invokeFunction<{ crm_person_id: string }>('demo-requests', {
    method: 'POST',
    path: `/${requestId}/sync-to-crm`,
  })
  if (error) throw toApiRequestError(error, 'CRM sync failed')
  return data ?? { crm_person_id: '' }
}

export function sourceLabel(source: string): string {
  switch (source) {
    case 'cal_com': return 'Cal.com'
    case 'vapi_phone': return 'Phone'
    case 'chat_widget': return 'Chat'
    case 'manual': return 'Manual'
    default: return source
  }
}

export function serviceTypeLabel(serviceType: string | null | undefined): string {
  switch (serviceType) {
    case 'mock_survey': return 'Mock Survey'
    case 'domain_audit': return 'Domain Audit'
    case 'qapi_design': return 'QAPI Design'
    case 'accreditation_prep': return 'Accreditation Prep'
    case 'compliance_retainer': return 'Compliance Retainer'
    case 'essentials_bundle': return 'Essentials Bundle'
    case 'accreditation_ready_bundle': return 'Accreditation Ready Bundle'
    case 'total_compliance_bundle': return 'Total Compliance Bundle'
    case 'general':
    case null:
    case undefined:
      return 'General'
    default:
      return serviceType
  }
}

export function statusColor(status: string): { bg: string; text: string } {
  switch (status) {
    case 'new': return { bg: 'bg-blue-100', text: 'text-blue-700' }
    case 'contacted': return { bg: 'bg-yellow-100', text: 'text-yellow-700' }
    case 'qualified': return { bg: 'bg-emerald-100', text: 'text-emerald-700' }
    case 'demo_scheduled': return { bg: 'bg-purple-100', text: 'text-purple-700' }
    case 'closed': return { bg: 'bg-slate-100', text: 'text-slate-500' }
    default: return { bg: 'bg-slate-100', text: 'text-slate-600' }
  }
}
